/**
 * AmenitiesListInterior — Weidner Via6 amenities page (interior row).
 *
 * Companion to `AmenitiesListCommunity`. Renders the second amenity
 * row (interior / apartment-group features) below the DividerBG
 * banner on the amenities page. See `_AmenitiesListRow.tsx` for the
 * rendering helper.
 *
 * Editor surface:
 *   - `amenitieslistinterior.heading` — the row heading (richtext).
 *     Default uses the cursive accent span on the leading word, with
 *     a trailing period to match the legacy treatment ("Interior
 *     amenities.").
 *   - `amenitieslistinterior.items` — interior amenities (defaults to
 *     the `amenities` collection filtered to `group === 'Apartment'`,
 *     matching the upstream rise filter; the source data uses
 *     "Apartment" as the group label for interior items).
 *   - `amenitieslistinterior.photos` — interior photo gallery
 *     (defaults to `photo_gallery` filtered to `title === 'Apartment'`).
 *
 * Visual: image panel on the right, mirroring the legacy rise default
 * position for the Apartment row.
 */

import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent, useCollectionBinding } from '@vibesites/runtime/react';
import {
  AmenitiesListRow,
  type AmenityItem,
  type GalleryPhoto,
} from './_AmenitiesListRow';

function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

const HEADING_DEFAULT_HTML =
  '<p><span class="vibe-cursive-accent">Interior</span> amenities.</p>';

export function AmenitiesListInterior() {
  const items = useCollectionBinding<AmenityItem>('amenitieslistinterior.items', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });

  const photos = useCollectionBinding<GalleryPhoto>('amenitieslistinterior.photos', {
    collection: 'photo_gallery',
    filter: { title: 'Apartment' },
    defaultLimit: 8,
  });

  return (
    <AmenitiesListRow
      headingNode={
        <RichTextContent
          path="amenitieslistinterior.heading"
          label="Heading"
          defaultValue={HEADING_DEFAULT_HTML}
          as="h3"
          className="amenities-col-heading"
        />
      }
      headingText={stripHtml(HEADING_DEFAULT_HTML) || 'Interior amenities'}
      items={items}
      photos={photos}
      imagePosition="right"
    />
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesListInterior',
  displayName: 'Amenities List — Interior',
  tokens: {
    'amenitieslistinterior.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue:
        '<p><span class="vibe-cursive-accent">Interior</span> amenities.</p>',
    },
  },
  collectionBindings: {
    'amenitieslistinterior.items': {
      label: 'Interior Amenities',
      collection: 'amenities',
      filter: { group: 'Apartment' },
    },
    'amenitieslistinterior.photos': {
      label: 'Interior Photos',
      collection: 'photo_gallery',
      filter: { title: 'Apartment' },
      defaultLimit: 8,
    },
  },
};

(AmenitiesListInterior as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default AmenitiesListInterior;
